"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Check, Loader, Shield, Zap, Smartphone, Search, MousePointerClick, RefreshCw, Phone, Users, MessageSquare, PhoneCall } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const features = [{
  icon: Zap,
  text: "Custom modern website design"
}, {
  icon: Shield,
  text: "12 months hosting included"
}, {
  icon: Shield,
  text: "12 months domain registration"
}, {
  icon: MousePointerClick,
  text: "Up to 5 pages"
}, {
  icon: Users,
  text: "1-hour branding session"
}, {
  icon: Smartphone,
  text: "Mobile responsive design"
}, {
  icon: Search,
  text: "Basic SEO optimisation"
}, {
  icon: MessageSquare,
  text: "Contact form integration"
}, {
  icon: PhoneCall,
  text: "Click-to-call"
}, {
  icon: RefreshCw,
  text: "Ongoing updates (5/month)"
}, {
  icon: Phone,
  text: "Unlimited technical calls"
}, {
  icon: Check,
  text: "Testimonials Section"
}];
function PricingForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-primary/20 flex items-center justify-center mb-4">
          <Check className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold text-foreground">Inquiry Received!</h3>
        <p className="text-muted-foreground max-w-xs mx-auto">{successMessage}</p>
        <Button asChild variant="outline" className="mt-6">
          <Link to="/">Return Home</Link>
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="full_name" rules={{
      required: "Full Name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Full Name</FormLabel>
            <FormControl>
              <Input placeholder="Jane Doe" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+61 2 1234 5678" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="business_name" render={({
      field
    }) => <FormItem>
            <FormLabel>Business Name <span className="text-muted-foreground text-xs font-normal ml-1">(Optional)</span></FormLabel>
            <FormControl>
              <Input placeholder="Acme Painting Services" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel>Additional Details / Questions <span className="text-muted-foreground text-xs font-normal ml-1">(Optional)</span></FormLabel>
            <FormControl>
              <textarea className="flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30 transition-all" placeholder="Tell us about your project or any specific requirements..." {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" className="w-full font-semibold text-base py-6" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Processing...
          </> : "Start My Website"}
      </Button>
      
      <p className="text-xs text-center text-muted-foreground mt-4">We'll get back to you within 48 hours to discuss your project.</p>
    </Form>;
}
export default function AdvancedPackagePricing() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  return <section id="advanced-package-pricing" className="dark bg-background text-foreground py-20 relative overflow-hidden ![margin:-69px_0px_0px_0px]" ref={sectionRef}>
      
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 dark:bg-primary/10 rounded-full blur-[100px] border border-transparent dark:border-primary/5" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-primary/5 dark:bg-primary/10 rounded-full blur-[120px] border border-transparent dark:border-primary/5" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className={`grid lg:grid-cols-2 gap-12 items-start transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          
          <div className="space-y-8 lg:sticky lg:top-24">
            <div>
              <div className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-sm font-medium text-primary ![margin:0px_0px_16px_0px]">
                Most Popular Choice
              </div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
                Advanced Package
              </h2>
              <p className="text-xl text-muted-foreground max-w-lg">
                A complete digital solution for growing businesses that need more pages and flexibility.
              </p>
            </div>

            <div className="flex items-baseline gap-2 pb-6 border-b border-border">
              <span className="text-5xl font-bold text-foreground">$1400</span>
              <span className="text-xl text-muted-foreground">+ GST</span>
            </div>

            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 rounded-lg bg-card border border-border/50 dark:bg-card dark:border-border/70">
                <span className="font-medium">Hosting & Maintenance</span>
                <span className="font-bold text-primary">$39/mo <span className="text-muted-foreground text-sm font-normal">+ GST</span></span>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-lg">What's Included:</h3>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {features.map((feature, i) => <li key={i} data-index={i} className="flex items-start gap-3 text-muted-foreground">
                      <div className="mt-1 rounded-full bg-primary/10 p-1">
                        <feature.icon className="size-3.5 text-primary" />
                      </div>
                      <span className="text-sm">{feature.text}</span>
                    </li>)}
                </ul>
              </div>
            </div>

            <div className="p-6 rounded-xl bg-secondary/30 border border-border/50 backdrop-blur-sm dark:bg-secondary/30 dark:border-border/70">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Shield className="size-4 text-primary" />
                100% Satisfaction Guarantee
              </h4>
              <p className="text-sm text-muted-foreground">
                We don't stop until you're happy with your design. Two rounds of design revisions included to ensure your vision comes to life.
              </p>
            </div>
          </div>

          <div className="lg:pl-8">
            <Card className="border-border/50 bg-card/50 backdrop-blur-xl shadow-2xl shadow-black/20 dark:bg-card/50 dark:border-border/70">
              <CardHeader className="space-y-1 pb-6 border-b border-border/50">
                <CardTitle className="text-2xl">Advanced Package Pricing Inquiry</CardTitle>
                <CardDescription>
                  Fill out the form below to get started. No payment required today.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <FormProvider formId="pricing_form_advanced_package" sectionName="advanced-package-pricing" defaultValues={{
                full_name: "",
                email: "",
                phone: "",
                business_name: "",
                message: ""
              }} validationRules={{
                full_name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true,
                  pattern: "phone"
                }
              }} submitText="Start My Website" successMessage="Thank you for your inquiry! We have received your details and will be in touch shortly to discuss your Advanced Package.">
                  <PricingForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}